import { z } from 'zod';

/**
 * Validation schema for property form fields
 *
 * Rules:
 * - Name: Required, non-empty string
 * - Metron ID: Required, exactly 10 digits
 */
export const propertyFormSchema = z.object({
  name: z
    .string()
    .min(1, 'Property name is required')
    .trim(),
  metronId: z
    .string()
    .regex(/^\d{10}$/, 'Metron ID must be exactly 10 digits'),
  masterSensorGroupId: z.number().nullable().optional(),
  childSensorGroupId: z.number().nullable().optional(),
});

export type PropertyFormData = z.infer<typeof propertyFormSchema>;

/**
 * Validate property form data and return errors
 *
 * @param data - Form data to validate
 * @returns Object with field errors, or empty object if valid
 *
 * @example
 * ```ts
 * const errors = validatePropertyForm({ name: '', metronId: '123' });
 * // Returns: { name: 'Property name is required', metronId: 'Metron ID must be exactly 10 digits' }
 * ```
 */
export function validatePropertyForm(
  data: Partial<PropertyFormData>,
): Record<string, string> {
  const result = propertyFormSchema.safeParse(data);

  if (result.success) {
    return {};
  }

  const errors: Record<string, string> = {};
  result.error.errors.forEach((err) => {
    const field = err.path[0] as string;
    errors[field] = err.message;
  });

  return errors;
}
